import { useStore } from "@/stores/useStore";
import { ActivityFeedItem } from "@/components/ActivityFeed/ActivityFeedItem";
import { ActivityFeedSearchControls } from "@/components/ActivityFeed/ActivityFeedSearchControls";
import { ActivityFeedPaginationControls } from "@/components/ActivityFeed/ActivityFeedPaginationControls";
import { useActivityFeedPagination } from "@/hooks/useActivityFeedPagination";
import { useActivityFeedAnalytics } from "@/hooks/useActivityFeedAnalytics";
import { firestoreToDateTime } from "@/lib/utils";
import { format } from "date-fns";
import { useMemo, useCallback } from "react";
import { ActivityFeedUnion } from "@/types/v2";

export const ActivityFeed = () => {
  const shop = useStore((s) => s.shop);
  const shopId = useStore((s) => s.shopId);

  const {
    activities,
    loading,
    hasMore,
    filters,
    applyFilters,
    loadMore,
    resetFilters,
  } = useActivityFeedPagination(shopId);

  const { trackFilterApplied, trackLoadMore } = useActivityFeedAnalytics();

  // Calculate if there are active filters for analytics
  const hasActiveFilters = useMemo(() => {
    return Object.keys(filters).some(
      (key) => filters[key as keyof typeof filters] !== undefined
    );
  }, [filters]);

  // Wrapped handler for filter application with analytics tracking
  const handleApplyFilters = useCallback(
    (newFilters: typeof filters) => {
      try {
        // Apply the filters first
        applyFilters(newFilters);

        // Track the filter application
        trackFilterApplied(newFilters);
      } catch (error) {
        console.error("Error applying filters:", error);
        // Still apply filters even if analytics fails
        applyFilters(newFilters);
      }
    },
    [applyFilters, trackFilterApplied]
  );

  // Wrapped handler for load more with analytics tracking
  const handleLoadMore = useCallback(() => {
    const startTime = performance.now();

    try {
      loadMore();

      // Track pagination - estimate page based on current items
      const currentPage = Math.ceil(activities.length / 20) + 1;
      const totalItemsAfterLoad = activities.length + 20; // Assuming 20 items per page
      const loadTime = performance.now() - startTime;

      trackLoadMore(
        currentPage,
        totalItemsAfterLoad,
        hasActiveFilters,
        loadTime
      );
    } catch (error) {
      console.error("Error loading more activities:", error);
      // Still load more even if analytics fails
      loadMore();
    }
  }, [loadMore, activities.length, hasActiveFilters, trackLoadMore]);

  const activityFeedSections = useMemo(() => {
    const groupedItems: { [date: string]: ActivityFeedUnion[] } = {};
    activities.forEach((item) => {
      const dateTime = firestoreToDateTime(item.createdAt);
      const date = dateTime ? format(dateTime, "MMMM dd, yyyy") : "";
      if (!groupedItems[date]) {
        groupedItems[date] = [];
      }
      groupedItems[date].push(item);
    });

    return Object.keys(groupedItems).map((date) => ({
      sectionName: date,
      items: groupedItems[date],
    }));
  }, [activities]);

  if (!shop) {
    return null;
  }

  return (
    <>
      <ActivityFeedSearchControls
        filters={filters}
        onFiltersChange={handleApplyFilters}
        onReset={resetFilters}
        loading={loading}
      />

      {activities.length > 0 ? (
        <>
          <div className="flex flex-grow flex-col gap-1 overflow-auto rounded-lg bg-slate-100 p-8 pt-2">
            {activityFeedSections.map((section) => (
              <div key={section.sectionName} className="flex flex-col">
                <div className="relative flex items-center justify-start py-2">
                  <div className="grid grid-cols-5 items-center gap-3 text-lg font-semibold text-accent lg:grid-cols-9">
                    <div className="relative h-full w-10">
                      {section.items.length > 0 && (
                        <div className="absolute left-1/2 top-1/2 h-full w-0.5 -translate-x-0.5 bg-border"></div>
                      )}
                      <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1.5 rounded-full bg-primary"></div>
                    </div>
                    <span className="col-span-4 lg:col-span-8">
                      {section.sectionName}
                    </span>
                  </div>
                </div>
                {section.items.map((item, index) => (
                  <ActivityFeedItem
                    key={item.id}
                    shop={shop}
                    item={item}
                    isFirst={index === 0}
                    isLast={index === section.items.length - 1}
                  />
                ))}
              </div>
            ))}
          </div>

          <ActivityFeedPaginationControls
            hasMore={hasMore}
            loading={loading}
            onLoadMore={handleLoadMore}
            totalLoaded={activities.length}
          />
        </>
      ) : !loading ? (
        <div className="py-8 text-center text-muted-foreground">
          No activity found matching your criteria.
        </div>
      ) : null}

      {loading && activities.length === 0 && (
        <div className="py-8 text-center text-muted-foreground">
          Loading activities...
        </div>
      )}
    </>
  );
};
